import { datadogRum } from '@datadog/browser-rum';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { DefaultError } from '@/common/components/error/DefaultError';
import { goToLogout } from '@/common/utils/authRedirect';

export const authErrorSeveritySchema = z.enum(['error', 'warning']);
export type AuthErrorSeverity = z.infer<typeof authErrorSeveritySchema>;

const reportBySeverity: Record<AuthErrorSeverity, (error: Error) => void> = {
  error: (error) =>
    datadogRum.addError(error, {
      source: 'auth0',
      type: 'authentication-error',
      stack: error.stack,
      message: error.message,
    }),
  warning: () => datadogRum.addAction('auth-error-warning'),
};

interface AuthErrorProps {
  error?: Error;
  severity?: AuthErrorSeverity;
  tryAgain?: () => void;
}

export const AuthError = ({ error, tryAgain, severity = 'error' }: AuthErrorProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (error) {
      reportBySeverity[severity](error);
    }
  }, [error, severity]);

  return (
    <DefaultError
      title={t(($) => $.common.errors.auth.title)}
      description={t(($) => $.common.errors.auth.description)}
      actions={[
        {
          label: t(($) => $.common.tryAgain),
          action: tryAgain ?? (() => goToLogout()),
        },
      ]}
    />
  );
};
